import React from 'react';
import type { MenuProps } from 'antd';
import { Layout, Menu } from 'antd';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { PlatformHeader } from '@doer/frontend-components/platform-header';

import { useAuthContext } from '../../../context/auth';
import { useSettingsContext } from '../../../context/settings';
import { MenuSections } from '../../../types/menu-sections';
import { CreativityType } from '../../../types/banner';
import { DashboardPage, CreateBannerPage, CreativityFormPage, LibraryPage } from '../../../pages';
import { ActionButton } from '@doer/frontend-components/buttons';
import { isValidCreativityType } from '../../../clients/registry';
import { TreeView } from '../../library/tree-view';
import { LibraryProvider } from '../../../context/library';
import { BindingCreativityEditorPage } from '../../../modules/creativity-editor/pages/editor-with-binding-page';
import { SocialAdminDashboardPage, SocialAdminUserPostsPage } from '../../../pages/social-admin';
import { ClientLogo } from '../client-logo';
import type { PlatformModule } from '@doer/frontend-components/platform-header';
import { PLATFORM_MODULES } from 'src/constants/platform-modules';
import { getBrandAccess } from 'src/clients/brand-access';
import { getClientLabel } from 'src/clients/client-options';
import type { ClientID } from 'src/types/banner/clients/clients';
import { useResponsiveSizes } from 'src/hooks/use-responsive-sizes';

const { Content } = Layout;
type MenuItem = Required<MenuProps>['items'][number];

const getMenuActiveKey = (pathname: string): MenuSections | undefined => {
  if (pathname === '/') return 'dashboard';
  if (pathname === '/create-asset') return 'create-asset';
  if (pathname.startsWith('/library')) return 'library';
  if (pathname.startsWith('/social-admin')) return 'social-admin';
  if (pathname.startsWith('/creativity-form/')) return pathname.slice(1) as MenuSections;
  if (pathname.startsWith('/creativity-editor/')) return pathname.slice(1) as MenuSections;
  return undefined;
};

const getActiveModuleId = (pathname: string, modules: PlatformModule[]): string => {
  // Rutas que pertenecen a un módulo pero no comparten prefijo con su `path` declarado.
  if (pathname.startsWith('/avatars-editor')) return 'digital-brand-ambassador';
  return modules.find(m => m.path !== '/' && pathname.startsWith(m.path))?.id ?? 'asset-multiplier';
};

export const AppLayout: React.FC = () => {
  const { isLoggedIn, loginUrl } = useAuthContext();

  if (!isLoggedIn) {
    return (
      <div className='login-page'>
        <div className='Button-container'>
          <ActionButton variant='secondary' size='L' onClick={() => (window.location.href = loginUrl)}>
            Ingresar
          </ActionButton>
        </div>
      </div>
    );
  }

  // Si está logueado, renderizar el layout autenticado
  return <AuthenticatedLayout />;
};

export const AppHeader: React.FC = () => {
  const { userInfo, signOut } = useAuthContext();
  const { settings } = useSettingsContext();
  const { fontSize, spacing } = useResponsiveSizes();
  const navigate = useNavigate();
  const location = useLocation();

  const activeKey = getMenuActiveKey(location.pathname);

  // Permisos por-marca (client settings). Gobiernan la pestaña Social y el módulo Avatars.
  const access = React.useMemo(() => getBrandAccess(settings), [settings]);

  const handleNavigate = (section: MenuSections) => {
    let path = '/';
    if (section === 'create-asset') path = '/create-asset';
    else if (section === 'library') path = '/library';
    else if (section === 'social-admin') path = '/social-admin';
    else if (section.startsWith('creativity-form/')) path = `/${section}`;
    else if (section.startsWith('creativity-editor/')) path = `/${section}`;
    navigate(path);
  };

  const items = React.useMemo(() => {
    const menuItems: MenuItem[] = [{ key: 'dashboard', label: 'Dashboard' }];
    if (!settings.disableBannerCreation) {
      menuItems.push({ key: 'create-asset', label: 'Plantillas' });
    }
    menuItems.push({ key: 'library', label: 'Librería' });
    if (access.showSocial) {
      menuItems.push({ key: 'social-admin', label: 'Social' });
    }
    return menuItems;
  }, [settings.disableBannerCreation, access.showSocial]);

  const navMenu = (
    <Menu
      mode='horizontal'
      selectedKeys={activeKey ? [activeKey] : []}
      onClick={({ key }) => handleNavigate(key as MenuSections)}
      items={items}
      className='custom-menu'
      style={{
        borderBottom: 'none',
        gap: spacing.xs,
        fontWeight: 500,
        background: 'transparent',
        fontSize: fontSize.xlg,
        userSelect: 'none'
      }}
    />
  );

  const clientId = userInfo?.['custom:clientId'];
  const modules = React.useMemo(
    () => PLATFORM_MODULES.filter(m => access.allowedModuleIds.includes(m.id)),
    [access.allowedModuleIds]
  );
  const activeModule = getActiveModuleId(location.pathname, modules);

  return (
    <PlatformHeader
      clientLogo={<ClientLogo clientId={clientId} />}
      modules={modules}
      activeModule={activeModule}
      navItems={navMenu}
      onModuleChange={moduleId => {
        const mod = modules.find(m => m.id === moduleId);
        if (mod) navigate(mod.path);
      }}
      onSignOut={signOut}
      userName={clientId ? getClientLabel(clientId as ClientID) : undefined}
      userEmail={userInfo?.email}
      drawerMenuItems={items}
      drawerActiveKey={activeKey}
      onDrawerMenuSelect={key => handleNavigate(key as MenuSections)}
      drawerExtraContent={activeKey === 'library' ? <TreeView darkMode={false} /> : undefined}
      environment={process.env.REACT_APP_ENV_SUFFIX === 'alt' ? 'alt' : undefined}
    />
  );
};

export const AuthenticatedLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useSettingsContext();
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  // Mismos permisos que el header: si la marca no tiene 'social' habilitado, navegar a
  // /social-admin por URL NO debe renderizar la vista (el botón oculto no alcanza como gate).
  const access = React.useMemo(() => getBrandAccess(settings), [settings]);

  const [activeKey, setActiveKey] = React.useState<MenuSections>(getMenuActiveKey(location.pathname) ?? 'dashboard');

  // Sincronizar activeKey con URL
  React.useEffect(() => {
    setActiveKey(getMenuActiveKey(location.pathname) ?? 'dashboard');
  }, [location.pathname]);

  // Scrollear al top al cambiar de sección
  React.useEffect(() => {
    if (activeKey === 'library') return;

    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [activeKey]);

  // Función que combina setState + navigate
  const handleNavigate = (section: MenuSections) => {
    let path = '/';

    if (section === 'create-asset') path = '/create-asset';
    else if (section === 'library') path = '/library';
    else if (section === 'social-admin') path = '/social-admin';
    else if (section.startsWith('creativity-form/')) path = `/${section}`;
    else if (section.startsWith('creativity-editor/')) path = `/${section}`;

    navigate(path);
  };

  // Componente wrapper para creativity con parámetros
  const CreativityFormWrapper = () => {
    const { creativityType } = useParams<{ creativityType: string }>();
    const decodedType = creativityType ? decodeURIComponent(creativityType) : '';

    if (!decodedType || !isValidCreativityType(decodedType)) {
      return <DashboardPage onNavigate={handleNavigate} />;
    }

    return <CreativityFormPage creativityType={decodedType as CreativityType} onNavigate={handleNavigate} />;
  };

  const getComponent = () => {
    if (activeKey === 'dashboard') {
      return <DashboardPage onNavigate={handleNavigate} />;
    }

    if (activeKey === 'create-asset') {
      return <CreateBannerPage onNavigate={handleNavigate} />;
    }

    if (activeKey === 'library') {
      return <LibraryPage />;
    }

    if (activeKey === 'social-admin') {
      // Permiso por-usuario: sin la feature 'social', no se renderiza (redirige a Dashboard).
      if (!access.showSocial) return <DashboardPage onNavigate={handleNavigate} />;
      // Dashboard (con stats + ranking unificados) y el drill-down por usuario.
      if (location.pathname.startsWith('/social-admin/user/')) return <SocialAdminUserPostsPage />;
      return <SocialAdminDashboardPage />;
    }

    if (activeKey.startsWith('creativity-form/')) {
      return <CreativityFormWrapper />;
    }

    if (activeKey.startsWith('creativity-editor/')) {
      return <BindingCreativityEditorPage />;
    }

    return <DashboardPage onNavigate={handleNavigate} />;
  };

  const layoutContent = (
    <Layout style={{ display: 'flex', height: '100vh' }}>
      <AppHeader />
      <Content ref={contentRef} style={{ overflow: 'auto', backgroundColor: 'white' }}>
        {getComponent()}
      </Content>
    </Layout>
  );

  return activeKey === 'library' ? <LibraryProvider>{layoutContent}</LibraryProvider> : layoutContent;
};
